const tabbables = [
    'button:enabled',
    'select:enabled',
    'textarea:enabled',
    'input:enabled',
    'a[href]',
    'area[href]',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[tabindex]',
    '[contenteditable]',
    '[autofocus]',
].join(',');
const keyboardSkips = ['[tabindex="-1"]'].join(',');
const delegationSkips = [
    '[data-skip-focus-delegation]',
    '[data-skip-focus-delegation] *',
    keyboardSkips,
].join(',');
const isFocusable = (node, skip) => {
    var _a, _b, _c, _d;
    return ((_b = (_a = node).matches) === null || _b === void 0 ? void 0 : _b.call(_a, tabbables)) &&
        !((node.tagName === 'INPUT' ||
            node.tagName === 'BUTTON') &&
            (node.type === 'hidden' ||
                node.disabled)) &&
        !(skip && ((_d = (_c = node).matches) === null || _d === void 0 ? void 0 : _d.call(_c, skip)));
};
const focusableElements = (el, skip) => {
    if (isFocusable(el, skip)) {
        return [el];
    }
    if (el.nodeName === 'SLOT') {
        return el
            .assignedNodes()
            .flatMap((n) => focusableElements(n, skip));
    }
    if ('shadowRoot' in el && el.shadowRoot) {
        return Array.from(el.shadowRoot.childNodes).flatMap((n) => focusableElements(n, skip));
    }
    return Array.from(el.childNodes).flatMap((n) => focusableElements(n, skip));
};
const activeElement = (el = document.activeElement) => {
    if (!el || isFocusable(el))
        return el;
    if (el.shadowRoot)
        return activeElement(el.shadowRoot.activeElement);
    return null;
};
const orderedFocusableElements = (el, skip) => focusableElements(el, skip).sort((a, b) => { var _a, _b; return (_b = (_a = a.tabIndex) !== null && _a !== void 0 ? _a : 0 - b.tabIndex) !== null && _b !== void 0 ? _b : 0; });
const firstFocusable = (els, skip) => {
    var _a;
    if (!els.length)
        return;
    return (_a = els.find((el) => !el.matches(skip))) !== null && _a !== void 0 ? _a : els[0];
};
const lastFocusable = (els, skip) => {
    return firstFocusable(els.reverse(), skip);
};
/**
 * Focuses first focusable decendant of passed HTMLElement if focus is not already within element
 */
const delegateFocus = (el, focusOptions) => {
    var _a;
    const els = orderedFocusableElements(el);
    const active = activeElement();
    if (active && els.includes(active))
        return;
    (_a = firstFocusable(els, delegationSkips)) === null || _a === void 0 ? void 0 : _a.focus(focusOptions);
};
/**
 * Focuses first focusable decendant of passed HTMLElement
 */
const focusFirst = (el, focusOptions) => {
    var _a;
    const els = orderedFocusableElements(el);
    (_a = firstFocusable(els, keyboardSkips)) === null || _a === void 0 ? void 0 : _a.focus(focusOptions);
};
/**
 * Focuses last focusable decendant of passed HTMLElement
 */
const focusLast = (el, focusOptions) => {
    var _a;
    const els = orderedFocusableElements(el);
    (_a = lastFocusable(els, keyboardSkips)) === null || _a === void 0 ? void 0 : _a.focus(focusOptions);
};
const isFragment = (node) => node.nodeType === node.DOCUMENT_FRAGMENT_NODE;
const hostPath = (node) => {
    let host = node;
    let rootNode = node.getRootNode();
    const path = new Map();
    path.set(rootNode, host);
    while (isFragment(rootNode)) {
        host = rootNode.host;
        rootNode = host.getRootNode();
        path.set(rootNode, host);
    }
    return path;
};
const stopPropagation = (event) => {
    event.stopPropagation();
};
/**
 * Allow focus within passed HTMLElement, even if outside of the focus trap.
 */
const allowFocus = (element) => {
    element.addEventListener('focusin', stopPropagation);
    return () => {
        element.removeEventListener('focusin', stopPropagation);
    };
};
/**
 * traps focus within passed HTMLElement. Returns a function to release focus trap.
 */
const trapFocus = (trap, focusOptions) => {
    const previousFocus = activeElement();
    delegateFocus(trap, focusOptions);
    const onFocusOutside = (event) => {
        event.preventDefault();
        const [trueTarget] = event.composedPath();
        if (!trueTarget) {
            return delegateFocus(trap, focusOptions);
        }
        const trapPath = hostPath(trap);
        const targetPath = hostPath(trueTarget);
        const sharedDocument = Array.from(targetPath.keys()).find((doc) => trapPath.has(doc));
        if (!sharedDocument) {
            return delegateFocus(trap, focusOptions);
        }
        const targetHost = targetPath.get(sharedDocument);
        const trapHost = trapPath.get(sharedDocument);
        const position = trapHost.compareDocumentPosition(targetHost);
        if (position & document.DOCUMENT_POSITION_FOLLOWING) {
            return focusFirst(trap, focusOptions);
        }
        if (position & document.DOCUMENT_POSITION_PRECEDING) {
            return focusLast(trap, focusOptions);
        }
        return delegateFocus(trap, focusOptions);
    };
    const trapTab = (event) => {
        if (event.key !== 'Tab')
            return;
        const [trueTarget] = event.composedPath();
        const els = orderedFocusableElements(trap, keyboardSkips);
        if (event.shiftKey) {
            if (els[0] === trueTarget) {
                event.preventDefault();
                event.stopPropagation();
                focusLast(trap, focusOptions);
            }
        }
        else if (els[els.length - 1] === trueTarget) {
            event.preventDefault();
            event.stopPropagation();
            focusFirst(trap, focusOptions);
        }
    };
    trap.addEventListener('focusin', stopPropagation);
    trap.addEventListener('keydown', trapTab, { capture: true });
    document.addEventListener('focusin', onFocusOutside);
    return (returnFocus = true) => {
        trap.removeEventListener('focusin', stopPropagation);
        trap.removeEventListener('keydown', trapTab, { capture: true });
        document.removeEventListener('focusin', onFocusOutside);
        if (returnFocus) {
            previousFocus === null || previousFocus === void 0 ? void 0 : previousFocus.focus(typeof returnFocus === 'boolean' ? undefined : returnFocus);
        }
    };
};

export { allowFocus, delegateFocus, focusFirst, focusLast, trapFocus };
